import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react'

import { Button, Loader, Alert } from '@mantine/core'

import BalticLogo from '@/components/icons/BalticLogo'
import Box from '@/components/ui/Box'
import DocumentTitle from '@/components/ui/DocumentTitle'

/**
 * OAuth 2.1 consent screen (Supabase Auth as the authorization server).
 *
 * Supabase redirects here with ?authorization_id=… when an OAuth client (an
 * MCP client like Claude) asks for access. The AuthGuard wrapping the route
 * has already ensured a session, so this page only has to fetch the request
 * details and let the person approve or deny. Approve/deny redirect the
 * browser back to the client on their own.
 */
const OAuthConsentPage = () => {
    const supabase = useSupabaseClient()
    const user = useUser()

    const [searchParams] = useSearchParams()
    const authorizationId = searchParams.get('authorization_id')

    const [details, setDetails] = useState(null)
    const [error, setError] = useState(null)
    const [deciding, setDeciding] = useState(false)

    useEffect(() => {
        if (!authorizationId) return

        supabase.auth.oauth
            .getAuthorizationDetails(authorizationId)
            .then(({ data, error }) => {
                if (error) return setError(error.message)

                // Already consented earlier: Supabase answers with a redirect
                // instead of details, send the browser straight back.
                if (!('authorization_id' in data)) {
                    window.location.href = data.redirect_url
                    return
                }

                setDetails(data)
            })
    }, [authorizationId])

    const decide = async (approved) => {
        setDeciding(true)

        const action = approved
            ? supabase.auth.oauth.approveAuthorization(authorizationId)
            : supabase.auth.oauth.denyAuthorization(authorizationId)

        const { error } = await action

        if (error) {
            setError(error.message)
            setDeciding(false)
        }
        // On success the SDK redirects the browser to the client itself.
    }

    return (
        <div className="min-h-dvh flex flex-col items-center bg-white p-2">
            <DocumentTitle title="BO - Autorisation" />

            <div className="flex flex-col gap-10 w-full max-w-[500px] mt-[100px]">
                <div className="mx-auto h-full flex items-center gap-3">
                    <BalticLogo className="w-[120px] text-gray-900" />
                </div>

                <Box>
                    {!authorizationId && (
                        <Alert color="red">
                            Lien d'autorisation invalide : paramètre{' '}
                            <code>authorization_id</code> manquant.
                        </Alert>
                    )}

                    {error && <Alert color="red">{error}</Alert>}

                    {authorizationId && !error && !details && (
                        <div className="flex justify-center py-8">
                            <Loader type="bars" size="sm" />
                        </div>
                    )}

                    {details && (
                        <div className="flex flex-col gap-5">
                            <div className="text-lg font-medium">
                                {details.client.name || 'Une application'}{' '}
                                demande l'accès au backoffice
                            </div>

                            <div className="text-sm text-gray-600 flex flex-col gap-1">
                                <div>
                                    Elle agira en tant que{' '}
                                    <span className="font-medium">
                                        {details.user.email || user?.email}
                                    </span>
                                    , avec les droits de ce compte.
                                </div>
                                <div>
                                    Après autorisation, retour vers :{' '}
                                    <code className="break-all">
                                        {details.redirect_uri}
                                    </code>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <Button
                                    onClick={() => decide(true)}
                                    loading={deciding}
                                    fullWidth
                                >
                                    Autoriser
                                </Button>
                                <Button
                                    onClick={() => decide(false)}
                                    disabled={deciding}
                                    variant="default"
                                    fullWidth
                                >
                                    Refuser
                                </Button>
                            </div>
                        </div>
                    )}
                </Box>
            </div>
        </div>
    )
}

export default OAuthConsentPage
